import { get } from "idb-keyval";
import ReactGA from "react-ga4";
import { GetAllDataVaccins } from "../types/api/allDatas";
import { GetCategory } from "../types/api/category";
import { GetSheet } from "../types/api/sheet";
import { UserRole } from "../types/api/user";
import { hasAcceptedCookies } from "./cookieConsent";

let analyticsInitialized = false;

const getGaCookieNames = (): string[] => {
  const trackingId = process.env.REACT_APP_GA4_TRACKING_ID;
  const measurementCookie = trackingId?.replace(/^G-/, "_ga_");
  const existingGaCookies = document.cookie
    .split(";")
    .map((cookie) => cookie.trim().split("=")[0])
    .filter((cookieName) => /^_ga/.test(cookieName));

  return Array.from(
    new Set([
      "_ga",
      "_gid",
      "_gat",
      ...(measurementCookie ? [measurementCookie] : []),
      ...existingGaCookies,
    ])
  );
};

const expireCookie = (name: string, domain?: string): void => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${
    domain ? `; domain=${domain}` : ""
  }`;
};

const clearAnalyticsCookies = (): void => {
  const hostnameParts = window.location.hostname.split(".");
  const domains = hostnameParts
    .map((_, index) => hostnameParts.slice(index).join("."))
    .filter((domain) => domain.includes("."));

  getGaCookieNames().forEach((cookieName) => {
    expireCookie(cookieName);
    domains.forEach((domain) => {
      expireCookie(cookieName, domain);
      expireCookie(cookieName, `.${domain}`);
    });
  });
};

export const grantAnalyticsConsent = (): void => {
  if (!analyticsInitialized) return;

  ReactGA.gtag("consent", "update", {
    analytics_storage: "granted",
  });
};

export const disableAnalytics = (): void => {
  if (analyticsInitialized) {
    ReactGA.gtag("consent", "update", {
      analytics_storage: "denied",
    });
  }

  clearAnalyticsCookies();
};

// Initialize Google Analytics only if user has consented
export const initializeAnalytics = (): boolean => {
  if (!hasAcceptedCookies()) {
    return false;
  }

  if (analyticsInitialized) {
    grantAnalyticsConsent();
    return true;
  }

  try {
    ReactGA.initialize([
      {
        trackingId: process.env.REACT_APP_GA4_TRACKING_ID!,
        gtagOptions: {
          debug_mode: true,
          send_page_view: false,
        },
      },
    ]);
    analyticsInitialized = true;
    grantAnalyticsConsent();
    return true;
  } catch (error) {
    console.error("Failed to initialize analytics:", error);
    return false;
  }
};

export const isAnalyticsInitialized = (): boolean => {
  return analyticsInitialized && hasAcceptedCookies();
};

const extractIdFromPath = (path: string, prefix: string): string | null => {
  const regex = new RegExp(`${prefix}/(\\d+)`);
  const match = path.match(regex);
  return match ? match[1] : null;
};

// Get specific content names from IndexedDB
const getContentName = async (path: string): Promise<string | null> => {
  try {
    // Category pages: /categorie/123
    if (path.startsWith("/categorie/")) {
      const categoryId = extractIdFromPath(path, "/categorie");
      if (categoryId) {
        const categoryData = await get<GetCategory>(`category-${categoryId}`);
        return categoryData?.category?.name || null;
      }
    }

    // Sheet pages: /fiche/123
    if (path.startsWith("/fiche/")) {
      const sheetId = extractIdFromPath(path, "/fiche");
      if (sheetId) {
        const sheetData = await get<GetSheet>(`sheet-${sheetId}`);
        return sheetData?.sheet?.name || null;
      }
    }

    // Vaccine pages: /vaccin/123
    if (path.startsWith("/vaccin/")) {
      const vaccinId = extractIdFromPath(path, "/vaccin");
      if (vaccinId) {
        const vaccinData = await get<GetAllDataVaccins>(`vaccin-${vaccinId}`);
        return vaccinData?.name || null;
      }
    }

    return null;
  } catch (error) {
    console.error("Error fetching content name:", error);
    return null;
  }
};

const getMainCategoryName = (mainCategoryId: number): string => {
  switch (mainCategoryId) {
    case 1:
      return "Infections";
    case 2:
      return "Micro-organismes";
    case 3:
      return "Anti-infectieux";
    case 4:
      return "Vaccins";
    default:
      return "";
  }
};

// Generate descriptive page titles based on path and actual content
const generatePageTitle = async (path: string): Promise<string> => {
  const baseTitle = "ePOPI";

  const cleanPath = path.split("?")[0];

  const contentName = await getContentName(cleanPath);

  const pathTitles: { [key: string]: string } = {
    "/": `${baseTitle} - Accueil`,
    "/connexion": `${baseTitle} - Connexion`,
    "/inscription": `${baseTitle} - Inscription`,
    "/mon-compte": `${baseTitle} - Mon Compte`,
    "/aide-administration": `${baseTitle} - Aide Administration`,
    "/aide-prescription": `${baseTitle} - Aide Prescription`,
    "/charte-epopi": `${baseTitle} - Charte ePOPI`,
    "/le-cmit": `${baseTitle} - Le CMIT`,
    "/comite-editorial": `${baseTitle} - Comité Editorial`,
    "/membres-du-cmit": `${baseTitle} - Membres du CMIT`,
    "/abonnement": `${baseTitle} - Abonnement`,
    "/abreviations": `${baseTitle} - Abréviations`,
    "/validation-email": `${baseTitle} - Validation Email`,
    "/reinitialiser-mon-mot-de-passe": `${baseTitle} - Réinitialiser Mot de Passe`,
    "/faq": `${baseTitle} - FAQ`,
    "/ouvrages": `${baseTitle} - Ouvrages`,
    "/contact": `${baseTitle} - Contact`,
    "/mentorpro": `${baseTitle} - MentorPro`,
    "/mentorpro/": `${baseTitle} - MentorPro`,
    "/mentions-legales": `${baseTitle} - Mentions Légales`,
    "/conditions-generales-utilisation": `${baseTitle} - Conditions Générales d'Utilisation`,
    "/conditions-generales-vente": `${baseTitle} - Conditions Générales de Vente`,
    "/politique-de-confidentialite": `${baseTitle} - Politique de Confidentialité`,
  };

  // Check for exact matches first
  if (pathTitles[cleanPath]) {
    return pathTitles[cleanPath];
  }

  // Check for partial matches with specific content names
  if (cleanPath.startsWith("/fiche/")) {
    if (contentName) {
      const sheetId = extractIdFromPath(cleanPath, "/fiche");
      if (sheetId) {
        try {
          const sheetData = await get<GetSheet>(`sheet-${sheetId}`);
          const categoryName = sheetData?.categories?.name;

          // Special handling for categories 5 and 6 - no category prefix since it's already in the content name
          if (
            categoryName === "Antibioprophylaxie en chirurgie" ||
            categoryName ===
              "Accidents d'exposition au sang et aux liquides biologiques"
          ) {
            return `${baseTitle} - ${contentName}`;
          }

          const mainCategoryName = getMainCategoryName(
            sheetData?.mainCategory?.id || 0
          );

          if (mainCategoryName) {
            return `${baseTitle} - ${mainCategoryName}: ${contentName}`;
          }
        } catch (error) {
          console.error("Error getting sheet hierarchy:", error);
        }
      }
      return `${baseTitle} - Infections: ${contentName}`;
    }
    return `${baseTitle} - Fiche`;
  }
  if (cleanPath.startsWith("/categorie/")) {
    if (contentName) {
      // Get category data to determine main category
      const categoryId = extractIdFromPath(cleanPath, "/categorie");
      if (categoryId) {
        try {
          const categoryData = await get<GetCategory>(`category-${categoryId}`);
          const mainCategoryName = getMainCategoryName(
            categoryData?.mainCategoryId || 0
          );

          // If this is a main category, show just the name
          if (mainCategoryName === contentName) {
            return `${baseTitle} - ${contentName}`;
          }
          // If this is a subcategory, show hierarchical title
          if (mainCategoryName) {
            return `${baseTitle} - ${mainCategoryName}: ${contentName}`;
          }
        } catch (error) {
          console.error("Error getting category hierarchy:", error);
        }
      }
      return `${baseTitle} - ${contentName}`;
    }
    return `${baseTitle} - Catégorie`;
  }
  if (cleanPath.startsWith("/vaccin/")) {
    return contentName
      ? `${baseTitle} - Vaccins: ${contentName}`
      : `${baseTitle} - Vaccin`;
  }
  if (cleanPath.startsWith("/aide-administration/")) {
    return `${baseTitle} - Aide Administration`;
  }
  if (cleanPath.startsWith("/aide-prescription/")) {
    return `${baseTitle} - Aide Prescription`;
  }
  if (cleanPath.startsWith("/connexion/")) {
    return `${baseTitle} - Connexion`;
  }
  if (cleanPath.startsWith("/inscription/")) {
    return `${baseTitle} - Inscription`;
  }
  if (cleanPath.startsWith("/mon-compte/")) {
    return `${baseTitle} - Mon Compte`;
  }
  if (cleanPath.startsWith("/abonnement/")) {
    return `${baseTitle} - Abonnement`;
  }
  if (cleanPath.startsWith("/validation-email/")) {
    return `${baseTitle} - Validation Email`;
  }
  if (cleanPath.startsWith("/reinitialiser-mon-mot-de-passe/")) {
    return `${baseTitle} - Réinitialiser Mot de Passe`;
  }

  // Default fallback
  return `${baseTitle} - ${cleanPath}`;
};

// Get establishment name based on user role and data
const getEstablishmentName = (user: any): string => {
  if (!user) return "Utilisateur Anonyme";

  // For ESTABLISHMENT users (IP/token login), the establishment name is in lastname
  if (user.role === UserRole.ESTABLISHMENT) {
    // For establishment users, the establishment name is stored in lastname
    // e.g., firstname: "Invité", lastname: "CHU de Lyon"
    return user.lastname || `establishment_${user.id}`;
  }

  // For INDIVIDUAL users, always show "Utilisateur individuel" regardless of their establishment field
  if (user.role === UserRole.INDIVIDUAL) {
    return "Utilisateur Individuel";
  }

  // For staff roles (ADMIN, MANAGER, EDITOR, AUTHOR, OLD_COLLABORATOR), they don't have establishments
  if (
    [
      UserRole.ADMIN,
      UserRole.MANAGER,
      UserRole.EDITOR,
      UserRole.AUTHOR,
      UserRole.OLD_COLLABORATOR,
    ].includes(user.role)
  ) {
    return user.establishment || "Équipe ePOPI";
  }

  return "Utilisateur Individuel";
};

// Get establishment ID based on user role and data
const getEstablishmentId = (user: any): string => {
  if (!user) return "Anonyme";

  // For ESTABLISHMENT users, their ID is the establishment ID
  if (user.role === UserRole.ESTABLISHMENT) {
    return user.id.toString();
  }

  // For other users, no specific establishment ID
  return "Aucun";
};

const getUserRoleLabel = (role: string): string => {
  const roleLabels: { [key: string]: string } = {
    ESTABLISHMENT: "Établissement",
    INDIVIDUAL: "Individuel",
    ADMIN: "Administrateur",
    MANAGER: "Gestionnaire",
    EDITOR: "Éditeur",
    AUTHOR: "Auteur",
    OLD_COLLABORATOR: "Ancien Collaborateur",
    VISITEUR: "Visiteur",
  };

  return roleLabels[role] || role;
};

// Track page views for route changes
export const trackPageView = async (
  path: string,
  title?: string,
  user?: any
) => {
  if (!isAnalyticsInitialized()) {
    return;
  }

  const pageTitle = title || (await generatePageTitle(path));

  // Update the browser's document title
  document.title = pageTitle;

  // Send standard page_view event with user context
  if (user) {
    const establishmentName = getEstablishmentName(user);
    const establishmentId = getEstablishmentId(user);

    ReactGA.gtag("event", "page_view", {
      page_location: window.location.href,
      page_path: path,
      page_title: pageTitle,
      user_id: user.id.toString(),
      user_type: "Authentifié",
      user_role: getUserRoleLabel(user.role),
      establishment_name: establishmentName,
      establishment_id: establishmentId,
    });
  } else {
    // Track anonymous users with custom parameters
    ReactGA.gtag("event", "page_view", {
      page_location: window.location.href,
      page_path: path,
      page_title: pageTitle,
      user_type: "Anonyme",
      user_role: getUserRoleLabel("VISITEUR"),
      establishment_name: "Utilisateur Anonyme",
      establishment_id: "Anonyme",
    });
  }
};

// Track custom events
export const trackEvent = (
  eventName: string,
  parameters: any = {},
  user?: any
) => {
  if (!isAnalyticsInitialized()) {
    return;
  }

  let eventParams;

  if (user) {
    eventParams = {
      ...parameters,
      user_id: user.id.toString(),
      user_type: "Authentifié",
      user_role: getUserRoleLabel(user.role),
      establishment_name: getEstablishmentName(user),
      establishment_id: getEstablishmentId(user),
    };
  } else {
    eventParams = {
      ...parameters,
      user_type: "Anonyme",
      user_role: getUserRoleLabel("VISITEUR"),
      establishment_name: "Utilisateur Anonyme",
      establishment_id: "Anonyme",
    };
  }

  ReactGA.gtag("event", eventName, eventParams);
};

// Track login events specifically
export const trackLogin = (user: any, method: "ip" | "token" | "password") => {
  if (!isAnalyticsInitialized()) {
    return;
  }

  const establishmentName = getEstablishmentName(user);
  const establishmentId = getEstablishmentId(user);

  const methodLabels = {
    ip: "IP",
    token: "Token",
    password: "Mot de Passe",
  };

  ReactGA.gtag("event", "connexion", {
    method: methodLabels[method],
    user_id: user.id.toString(),
    user_type: "Authentifié",
    user_role: getUserRoleLabel(user.role),
    establishment_name: establishmentName,
    establishment_id: establishmentId,
  });
};

// Track logout events
export const trackLogout = (user?: any) => {
  if (!isAnalyticsInitialized()) {
    return;
  }

  ReactGA.gtag("event", "deconnexion", {
    ...(user && {
      user_id: user.id.toString(),
      user_type: "Authentifié",
      user_role: getUserRoleLabel(user.role),
      establishment_name: getEstablishmentName(user),
    }),
  });
};

// Track content engagement
export const trackContentView = (
  contentType: string,
  contentId: string,
  contentName: string,
  user?: any
) => {
  if (!isAnalyticsInitialized()) {
    return;
  }

  ReactGA.gtag("event", "consultation_contenu", {
    content_type: contentType,
    content_id: contentId,
    content_name: contentName,
    ...(user && {
      user_id: user.id.toString(),
      user_type: "Authentifié",
      user_role: getUserRoleLabel(user.role),
      establishment_name: getEstablishmentName(user),
      establishment_id: getEstablishmentId(user),
    }),
  });
};

// Set user properties in GA4 for better user segmentation
export const setUserProperties = (user: any) => {
  if (!isAnalyticsInitialized()) {
    return;
  }

  if (!user) return;

  const establishmentName = getEstablishmentName(user);
  const establishmentId = getEstablishmentId(user);

  ReactGA.gtag("config", process.env.REACT_APP_GA4_TRACKING_ID!, {
    user_id: user.id.toString(),
    user_properties: {
      user_role: getUserRoleLabel(user.role),
      establishment_name: establishmentName,
      establishment_id: establishmentId,
      user_type: "Authentifié",
    },
  });
};

// Debug function to test analytics tracking (for development only)
export const debugAnalytics = (user?: any) => {
  if (!isAnalyticsInitialized()) {
    return;
  }

  if (user) {
    const establishmentName = getEstablishmentName(user);
    const establishmentId = getEstablishmentId(user);

    // Test sending analytics event
    ReactGA.gtag("event", "test_analytics", {
      user_id: user.id.toString(),
      user_type: "Authentifié",
      user_role: getUserRoleLabel(user.role),
      establishment_name: establishmentName,
      establishment_id: establishmentId,
    });
  } else {
    ReactGA.gtag("event", "test_analytics", {
      user_type: "Anonyme",
      user_role: getUserRoleLabel("VISITEUR"),
      establishment_name: "Utilisateur Anonyme",
      establishment_id: "Anonyme",
    });
  }
};
